import { defineComponent } from 'vue';
import dayjs from 'dayjs';
import BaseComponent from './baseFormMixin.jsx';
import utils from '@/Shared/utils.jsx';
import Calendar from '@/Application/components/vuecontrols/calendar.vue';
import { careWindowsToMomentTimezoneMap } from '@/Services/locale';

export default defineComponent({
    name: 'sform-daterangepicker',
    mixins: [BaseComponent],
    components: { Calendar },
    data: () => ({
        titleRenderer: null,
        defaultRenderer: null,
        timeZoneRenderer: null,
    }),
    props: {},
    created() {},
    mounted() {
        this.loadComplete();
        this.$nextTick(() => this.syncInitialValue());
    },
    computed: {
        default: function () {
            try {
                if (this.defaultRenderer === null && (this.itemvalue || this.element.default || this.element.schema.default))
                    this.defaultRenderer = utils.compile(this, this.itemvalue || this.element.default || this.element.schema.default);

                if (this.defaultRenderer)
                    return utils.evaluate(this.defaultRenderer, this);
                else
                    return '';
            } catch (e) {
                utils.warn('Default could not evaluate expression: ' + (this.element.default || this.element.schema?.default) + '; ' + e);
                return '';
            }
        },
        timeZone: function() {
            try {
                if (this.timeZoneRenderer === null && this.element.schema.timeZone)
                    this.timeZoneRenderer = utils.compile(this, this.element.schema.timeZone);
                if (this.timeZoneRenderer){
                    var passedTimeZone = utils.evaluate(this.timeZoneRenderer, this);
                    var dayjsTimezone = careWindowsToMomentTimezoneMap[passedTimeZone];
                    return dayjsTimezone;
                }
                else
                    return "";
            }
            catch (e)
            {
                utils.warn('Default could not evaluate expression: ' + this.element.schema.timeZone + '; ' + e);
                return '';
            }
        },
        calendarValue() {
            const d = this.default;
            if (d == null || d === '') return null;
            if (typeof d === 'object' && d.startDate != null && d.endDate != null) return d;
            if (typeof d === 'string') {
                try {
                    const parsed = JSON.parse(d);
                    if (parsed && parsed.startDate != null && parsed.endDate != null) return parsed;
                } catch (_) {}
            }
            return null;
        },
        fieldLabel() {
            if (this.notitle)
                return undefined;
            return this.element?.title
                || this.element?.schema?.placeholder
                || this.labelText
                || this.Title
                || 'Select Date Range';
        },
        hintText() {
            return this.element?.schema?.description || this.element?.description || '';
        },
        hideBottomSpace() {
            return this.appearance?.hideDetails === true;
        },
        // QField.model-value (calendar.vue's fieldModelValue) is the formatted
        // "start - end" display string, not the real {startDate,endDate} object.
        // Quasar calls each rule with that bound string, so the mixin's generic
        // rules compare a string against the object schema and always fail
        // (CCDEV-18145). Match formSingleDatePicker.jsx: validate this.itemvalue.
        rules() {
            const r = [];
            if (this.element?.required)
                r.push(() => {
                    const v = this.itemvalue;
                    if (v == null || v === '' || (typeof v === 'object' && (v.startDate == null || v.endDate == null)))
                        return 'Required';
                    return true;
                });

            if (this.element?.schema)
                r.push(() => {
                    const errors = utils.schema.performValidation(this.element.schema, this.itemvalue);
                    return errors.length > 0 ? errors[0].error : true;
                });

            return r;
        },
    },
    methods: {
        applyDateRangeHandler(payload) {
            this.sync(payload);
        },
        notifyFormChanged() {
            let p = this.$parent;
            while (p && (!('cc_form_changed_indicator' in p) || !p.cc_form_changed_active))
                p = p.$parent;

            p?.formChanged?.();
        },
        // calendar.vue paints today when the model has no range, and only emits on Apply.
        // A stored {startDate,endDate} or a JSON string of one wins. Otherwise post the
        // same today range the field is already showing (Vue2 applySelection on create).
        storedDateRange(value) {
            if (value && typeof value === 'object' && value.startDate != null && value.endDate != null)
                return value;
            if (typeof value === 'string') {
                try {
                    const parsed = JSON.parse(value);
                    if (parsed && parsed.startDate != null && parsed.endDate != null)
                        return parsed;
                } catch (_) {}
            }
            return null;
        },
        todayRange() {
            const now = dayjs();
            return {
                startDate: now.format('YYYY-MM-DD') + 'T00:00:00',
                endDate: now.format('YYYY-MM-DD') + 'T23:59:59',
            };
        },
        syncInitialValue() {
            if (this.noinput || this.appearance?.readonly)
                return;

            const initial = this.storedDateRange(this.itemvalue) || this.calendarValue || this.todayRange();
            this.applyDateRangeHandler(initial);
            this.$nextTick(() => this.notifyFormChanged());
        },
    },
    render() {
        try {
            let calendar = (
                <Calendar
                    ref="form"
                    myclass="sform-daterangepicker-input sform-input-white c-formDateRangePicker"
                    style={{ width: '100%' }}
                    value={this.itemvalue || this.calendarValue}
                    modelValue={this.itemvalue}
                    rules={this.rules} {...this.compilerErrorFieldProps}
                    label={this.fieldLabel}
                    isactive={!this.noinput}
                    allowFutureDate={this.element?.formatData?.AllowFutureDate}
                    allowPastDate={this.element?.formatData?.AllowPastDate}
                    message={this.hintText}
                    allowTime={this.element?.formatData?.AllowTime}
                    onChange={this.applyDateRangeHandler}
                    onBlur={this.onBlur}
                    onFocus={this.onFocus}
                    readonly={this.appearance?.readonly || this.noinput}
                    timeZone={this.timeZone}
                    hideBottomSpace={this.hideBottomSpace}
                    bottomSlots={!this.hideBottomSpace}
                    hide-hint
                />
            );

            if (this.appearance)
                utils.applyVue2ToVue3Props(calendar, this.appearance);

            return calendar;
        } catch (e) {
            utils.error('DateRangePicker Render failed', e);
            return <div>DateRangePicker Failed to Render {e.message}</div>;
        }
    }
});
